import { useEffect, useState } from 'react';
import { suggestCounting } from '@utils/api/videoProjects';
import { framesFromStills, framesFromVideo } from '@utils/video/sampleFrames';

const FRAMES = 5;
// the backend gives the assistant 20 s (scene_suggest.TIMEOUT_S); reading the frames comes on top
const LOOK_MAX_MS = 30000;

// /dev/video-wizard?suggest=mock answers without a backend
const MOCK = {
	subject: 'reindeer',
	scene: 'walking in a long column across snow',
	in_view: 100,
	camera: 'hovering',
	mode: 'line',
	alternatives: ['snowmobile', 'person'],
	confidence: 'high',
};
const mocked = () =>
	import.meta.env.DEV && new URLSearchParams(window.location.search).get('suggest') === 'mock';

/**
 * A suggestion of what to count, asked once per chosen video as soon as its length and size are known:
 * { status: 'idle' | 'looking' | 'ready' | 'none', suggestion }. Any failure is 'none'; the wizard works without.
 */
const useCountingSuggestion = ({ videoFile, videoURL, videoMetadata, stills }) => {
	const [state, setState] = useState({ status: 'idle', suggestion: null });
	const ready = videoMetadata?.duration > 0 && videoMetadata?.width > 0;
	const duration = videoMetadata?.duration;

	useEffect(() => {
		setState({ status: 'idle', suggestion: null });
	}, [videoFile]);

	useEffect(() => {
		if (!videoFile || !ready) return undefined;
		let cancelled = false;
		setState({ status: 'looking', suggestion: null });
		// Upload waits while this looks: never longer than LOOK_MAX_MS (a late answer is then not used)
		const timer = setTimeout(() => {
			if (cancelled) return;
			cancelled = true;
			setState({ status: 'none', suggestion: null });
		}, LOOK_MAX_MS);
		(async () => {
			try {
				if (mocked()) {
					await new Promise(r => setTimeout(r, 1200));
					if (!cancelled) setState({ status: 'ready', suggestion: MOCK });
					return;
				}
				const frames = stills
					? framesFromStills(stills, FRAMES)
					: await framesFromVideo(videoURL, duration, FRAMES);
				if (!frames.length) throw new Error('no frames');
				const suggestion = await suggestCounting({ frames, durationSeconds: duration });
				if (!cancelled)
					setState(
						suggestion?.subject
							? { status: 'ready', suggestion }
							: { status: 'none', suggestion: null }
					);
			} catch {
				if (!cancelled) setState({ status: 'none', suggestion: null });
			}
		})();
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [videoFile, videoURL, ready, duration, stills]);

	return state;
};

export default useCountingSuggestion;
